import stringify from 'fast-json-stable-stringify';
import { defaultEntityRegistry } from './entityRegistry';
import memoizeOne from 'react-utils/memoizeOne';
/**
 * Produce an instance key that uniquely identifies an instance.
 *
 * Uses length-prefixed encoding to avoid problems with folks using
 * our delimiter in their typeName/instance information.
 */
export const createInstanceKey = (typeName, instanceIdentifier) => `${typeName.length}:${typeName}:${instanceIdentifier}`;

/**
 * Parse the typeName out of an instance key
 */
export const parseTypeName = memoizeOne(key => {
  const colonIndex = key.indexOf(':');
  if (colonIndex === -1) {
    throw Error(`Invalid instance key: ${key}`);
  }
  const typeNameLength = parseInt(key.substring(0, colonIndex), 10);

  // typeName starts after the first colon
  const typeNameStart = colonIndex + 1;
  const typeNameEnd = typeNameStart + typeNameLength;
  return key.substring(typeNameStart, typeNameEnd);
});

/**
 * Create a basic entity definition. Must be statically analyzable (i.e called in module scope). This will be enforced.
 *
 * Automatically registers the entity with the global registry.
 *
 * @param typeName - A string that uniquely identifies the entity's type
 * @param defaultResolver - The default resolver used for this entity. Can be overridden on client construction
 * @param identifyInstance - A function that extracts a canonical identity string from an entity's params (defaults to a stable stringify of all params)
 * @param hasChanged - A function that semantically compares two instances of an entity (defaults to a simple reference comparison)
 *
 * @example
 * ```ts
 * // Define a "User" entity derived from a promise-based fetcher
 *
 * const fetchUser = (httpClient, { userId }: { userId: number }) =>
 *   httpClient.get<User>(`users/v1/users/${userId}`);
 *
 * const userResolver = fromPromise('fetchUser', fetchUser);
 *
 * const User = createEntity({
 *   typeName: 'myEntity',
 *   defaultResolver: userResolver,
 *   hasChanged: (current, next) => current.updatedAt < next.updatedAt, // Compare updated timestamps
 * });
 *
 * // pass to useEntity in React
 * const {
 *   data: user,
 *   loading,
 *   error,
 *   stale,
 * } = useEntity(User, { params: { userId: 123 } });
 * ```
 */
export const defineEntity = ({
  typeName,
  defaultResolver,
  identifyInstance = stringify,
  hasChanged = (current, next) => current !== next,
  __registry = defaultEntityRegistry
}) => {
  const definition = Object.freeze({
    typeName,
    defaultResolver,
    identifyInstance: params => createInstanceKey(typeName, identifyInstance(params)),
    hasChanged
  });
  __registry.registerEntity(definition);
  return definition;
};